import React, { useState } from 'react';
import { ClipboardCheck, PenTool, Scissors, Target, Calendar, Star } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const FlipCard = ({ step, index, hoverHint }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const Icon = step.icon;

  return (
    <div
      className="h-72 perspective-1000"
      onMouseEnter={() => setIsFlipped(true)}
      onMouseLeave={() => setIsFlipped(false)}
      data-testid={`flip-card-${index}`}
    >
      <div
        className={`relative w-full h-full transition-transform duration-500 transform-style-preserve-3d ${
          isFlipped ? 'rotate-y-180' : ''
        }`}
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* Front Face */}
        <div
          className="absolute inset-0 backface-hidden rounded-2xl bg-[#F9F6F4] border border-slate-200 p-6 flex flex-col items-center justify-center text-center"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-10 h-10 bg-[#009d9f] rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
            {step.number}
          </div>
          <div className="w-16 h-16 rounded-full bg-[#009d9f]/10 flex items-center justify-center mb-4 mt-2">
            <Icon className="w-8 h-8 text-[#009d9f]" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {step.frontTitle}
          </h3>
          <p className="text-slate-500 text-sm">{step.frontSubtitle}</p>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-slate-400 flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>{hoverHint}</span>
          </div>
        </div>

        {/* Back Face */}
        <div
          className="absolute inset-0 backface-hidden rounded-2xl bg-[#009d9f] p-6 flex flex-col items-center justify-center text-center text-white"
          style={{ 
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)'
          }}
        >
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-10 h-10 bg-white rounded-full flex items-center justify-center text-[#009d9f] font-bold text-lg shadow-lg">
            {step.number}
          </div>
          <h3 className="text-xl font-bold mb-4 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {step.backTitle}
          </h3>
          <p className="text-white/90 text-sm leading-relaxed">{step.backDescription}</p>
        </div>
      </div>
    </div>
  );
};

const ProcessSection = () => {
  const { t } = useLanguage();

  const steps = [
    { id: 1, number: '1', icon: ClipboardCheck, frontTitle: t('process.step1'), frontSubtitle: t('process.step1Sub'), backTitle: t('process.step1'), backDescription: t('process.step1Desc') },
    { id: 2, number: '2', icon: PenTool, frontTitle: t('process.step2'), frontSubtitle: t('process.step2Sub'), backTitle: t('process.step2'), backDescription: t('process.step2Desc') },
    { id: 3, number: '3', icon: Scissors, frontTitle: t('process.step3'), frontSubtitle: t('process.step3Sub'), backTitle: t('process.step3'), backDescription: t('process.step3Desc') },
    { id: 4, number: '4', icon: Target, frontTitle: t('process.step4'), frontSubtitle: t('process.step4Sub'), backTitle: t('process.step4'), backDescription: t('process.step4Desc') },
    { id: 5, number: '5', icon: Calendar, frontTitle: t('process.step5'), frontSubtitle: t('process.step5Sub'), backTitle: t('process.step5'), backDescription: t('process.step5Desc') },
    { id: 6, number: '6', icon: Star, frontTitle: t('process.step6'), frontSubtitle: t('process.step6Sub'), backTitle: t('process.step6'), backDescription: t('process.step6Desc') }
  ];

  return (
    <section id="proceso" className="py-20 md:py-28 bg-white" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-sm font-semibold tracking-wider uppercase text-[#009d9f] mb-4">
            {t('process.tagline')}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('process.title')}{' '}
            <span className="text-[#009d9f]">{t('process.titleHighlight')}</span>
          </h2>
          <p className="text-lg text-slate-600 mt-4">{t('process.description')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pt-6" data-testid="process-grid">
          {steps.map((step, index) => (
            <FlipCard key={step.id} step={step} index={index} hoverHint={t('process.hoverHint')} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
